import React, { useState, useEffect } from 'react';
import game from '../games';
import {
  Box,
  Typography,
  Button,
  TextField,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import GoogleIcon from '@mui/icons-material/Google';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import firebaseService, { DEMO_MODE, auth } from '../services/FirebaseService';

/* ── animations ── */
const pulseGlow = keyframes`
  0%, 100% { opacity: 0.6; }
  50%       { opacity: 1; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

/* ── styled components ── */
const PageWrapper = styled(Box)({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  backgroundImage: 'url(/login-bg.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center top',
  backgroundRepeat: 'no-repeat',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, rgba(8,8,8,0.55) 0%, rgba(8,8,8,0.80) 60%, rgba(8,8,8,0.97) 100%)',
    zIndex: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(0,200,220,0.07) 0%, transparent 70%)',
    animation: `${pulseGlow} 4s ease-in-out infinite`,
    zIndex: 0,
    pointerEvents: 'none',
  },
});

const Content = styled(Box)({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  padding: '40px 16px',
});

const TitleBlock = styled(Box)({
  textAlign: 'center',
  marginBottom: 36,
  animation: `${fadeUp} 0.6s ease both`,
  animationDelay: '0.1s',
});

const Card = styled(Box)({
  width: '100%',
  maxWidth: 420,
  background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
  border: '1px solid rgba(0,210,230,0.18)',
  borderRadius: 18,
  padding: '38px 36px',
  backdropFilter: 'blur(18px)',
  boxShadow: '0 12px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.07)',
  animation: `${fadeUp} 0.7s ease both`,
  animationDelay: '0.25s',
});

const OrDivider = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  margin: '18px 0',
  '& span': {
    flex: 1,
    height: 1,
    background: 'linear-gradient(90deg, transparent, rgba(0,210,230,0.25), transparent)',
  },
});

const GoogleBtn = styled(Button)({
  width: '100%',
  padding: '13px 24px',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: 0.4,
  background: 'linear-gradient(135deg, #c62828 0%, #e53935 50%, #c62828 100%)',
  backgroundSize: '200% auto',
  color: '#fff',
  boxShadow: '0 4px 24px rgba(198,40,40,0.45)',
  transition: 'transform 0.15s, box-shadow 0.15s, background-position 0.4s',
  '&:hover': {
    backgroundPosition: 'right center',
    boxShadow: '0 6px 32px rgba(229,57,53,0.65)',
    transform: 'translateY(-1px)',
  },
  '&:active': { transform: 'translateY(0)' },
});

const EmailBtn = styled(Button)({
  width: '100%',
  padding: '13px 24px',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: 0.4,
  background: 'transparent',
  border: '1px solid rgba(0,210,230,0.35)',
  color: 'rgba(0,230,255,0.9)',
  boxShadow: '0 2px 16px rgba(0,200,220,0.12)',
  transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
  '&:hover': {
    background: 'rgba(0,210,230,0.08)',
    borderColor: 'rgba(0,210,230,0.6)',
    boxShadow: '0 4px 24px rgba(0,200,220,0.25)',
  },
});

const StyledTextField = styled(TextField)({
  marginBottom: 14,
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    background: 'rgba(255,255,255,0.04)',
    '& fieldset': { borderColor: 'rgba(0,210,230,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(0,210,230,0.45)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(0,210,230,0.7)' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.45)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(0,230,255,0.85)' },
  '& input': { color: '#fff' },
});

const SignInBtn = styled(Button)({
  width: '100%',
  padding: '13px 24px',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: 0.4,
  background: 'linear-gradient(135deg, #00838f 0%, #00acc1 50%, #00838f 100%)',
  backgroundSize: '200% auto',
  color: '#fff',
  boxShadow: '0 4px 24px rgba(0,172,193,0.4)',
  transition: 'transform 0.15s, box-shadow 0.15s, background-position 0.4s',
  '&:hover': {
    backgroundPosition: 'right center',
    boxShadow: '0 6px 32px rgba(0,172,193,0.65)',
    transform: 'translateY(-1px)',
  },
  '&:active': { transform: 'translateY(0)' },
});

const DemoBtn = styled(Button)({
  width: '100%',
  padding: '13px 24px',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 700,
  background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)',
  color: '#fff',
  boxShadow: '0 4px 20px rgba(76,175,80,0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #388e3c 0%, #43a047 100%)',
    boxShadow: '0 6px 28px rgba(76,175,80,0.5)',
  },
});

const Footer = styled(Box)({
  position: 'absolute',
  bottom: 18,
  left: 0,
  right: 0,
  textAlign: 'center',
  color: 'rgba(255,255,255,0.18)',
  fontSize: 12,
  zIndex: 1,
  animation: `${fadeUp} 0.8s ease both`,
  animationDelay: '0.5s',
});

/* ── component ── */
const LandingPage = () => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth) return;
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const hasAccess = await firebaseService.checkCurrentUserAccess();
          if (!hasAccess) return;
          const returnUrl = sessionStorage.getItem('returnUrl');
          if (returnUrl) {
            sessionStorage.removeItem('returnUrl');
            navigate(returnUrl);
          } else {
            navigate('/drafts');
          }
        } catch (err) {
          console.error('Access check error:', err);
        }
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('accessDenied') === 'true') {
      showSnackbar('Your email has been removed from the allowed list. Please contact the administrator.', 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      await firebaseService.signInWithGoogle();
    } catch (err) {
      showSnackbar(err.message, 'error');
    }
  };

  const handleEmailSignIn = async () => {
    if (!email || !password) { setError('Please fill in all fields'); return; }
    try {
      await firebaseService.signIn(email, password);
      setShowEmailForm(false);
      resetForm();
    } catch (err) {
      let msg = 'Authentication failed';
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') msg = 'Invalid email or password';
      else if (err.code === 'auth/invalid-email') msg = 'Invalid email format';
      else msg = err.message;
      setError(msg);
      showSnackbar(msg, 'error');
    }
  };

  const resetForm = () => { setEmail(''); setPassword(''); setError(''); };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <PageWrapper>
      <Content>
        {/* Title */}
        <TitleBlock>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              letterSpacing: 1,
              lineHeight: 1.1,
              mb: 1,
              background: 'linear-gradient(135deg, #ff5252 0%, #ff1744 40%, #ff6e6e 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: `${shimmer} 4s linear infinite`,
              fontSize: { xs: '2rem', sm: '2.8rem', md: '3.2rem' },
            }}
          >
            {game.name}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: 2,
              color: 'rgba(255,255,255,0.92)',
              fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.75rem' },
              mb: 1,
            }}
          >
            Draft Planner / Simulator
          </Typography>
          <Typography
            sx={{
              color: 'rgba(0,230,255,0.75)',
              fontSize: { xs: 13, sm: 15 },
              letterSpacing: 3,
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            Plan · Refine · Execute
          </Typography>
        </TitleBlock>

        {/* Auth Card */}
        <Card>
          {DEMO_MODE ? (
            <DemoBtn variant="contained" onClick={() => navigate('/draft/new')}>
              Start Using App (Local Files Only)
            </DemoBtn>
          ) : !showEmailForm ? (
            <>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: 13,
                  textAlign: 'center',
                  mb: 3,
                  letterSpacing: 0.5,
                }}
              >
                Sign in to access your drafts
              </Typography>

              <GoogleBtn variant="contained" startIcon={<GoogleIcon />} onClick={handleGoogleSignIn}>
                Continue with Google
              </GoogleBtn>

              <OrDivider sx={{ mt: 2 }}>
                <span />
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, letterSpacing: 1 }}>OR</Typography>
                <span />
              </OrDivider>

              <EmailBtn variant="outlined" startIcon={<EmailIcon />} onClick={() => setShowEmailForm(true)}>
                Sign in with Email
              </EmailBtn>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 15,
                  fontWeight: 700,
                  textAlign: 'center',
                  mb: 3,
                  letterSpacing: 0.5,
                }}
              >
                Sign In with Email
              </Typography>

              <Box
                component="form"
                onSubmit={(e) => { e.preventDefault(); handleEmailSignIn(); }}
                sx={{ width: '100%' }}
              >
                <StyledTextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'rgba(0,210,230,0.5)', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'rgba(0,210,230,0.5)', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {error && (
                  <Typography sx={{ color: '#ff6e6e', fontSize: 13, mb: 1, textAlign: 'center' }}>
                    {error}
                  </Typography>
                )}

                <SignInBtn type="submit" variant="contained" sx={{ mb: 1.5 }}>
                  Sign In
                </SignInBtn>
              </Box>

              <Button
                fullWidth
                onClick={() => { setShowEmailForm(false); resetForm(); }}
                sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, textTransform: 'none' }}
              >
                ← Back
              </Button>
            </>
          )}
        </Card>
      </Content>

      <Footer>© {new Date().getFullYear()} {game.name} Draft Planner / Simulator</Footer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </PageWrapper>
  );
};

export default LandingPage;
